import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  useFetchApiAction,
  useFetchMultipartAction,
} from "../helpers/fetch-graphql";
import { useSetAtom } from "jotai";
import { activePopupState } from "../state/components/popup";
import Queries from "../params/queries";
import Mutations from "../params/mutations";
import { liveDashboard, liveLog } from "../helpers/live-graphql";
import {
  SUCCESS_DISMISS_MS,
  TAB_DEFS,
  TabIcon,
  accountKeyHidden,
  configToSettingsForm,
  isSuspendedAccount,
  mentionsHiddenAccount,
} from "./shared";
import ChallengeBanner from "./shell/ChallengeBanner";
import HeaderAlert from "./shell/HeaderAlert";
import Accounts from "./Accounts";
import Phones from "./Phones";
import Models from "./Models";
import Progress from "./Progress";
import Messages from "./Messages";
import Followers from "./Followers";
import Migration from "./Migration";
import Logs from "./Logs";
import Settings from "./Settings";
import type {
  AccountRow,
  ApiResult,
  Dashboard,
  LogRow,
  SettingsForm,
} from "../params/types";
import "../scss/pages/app.scss";

const NO_ACCOUNTS: AccountRow[] = [];

const AppShell = ({ onLogout }: { onLogout?: () => void }) => {
  const fetchApi = useFetchApiAction();
  const fetchMultipart = useFetchMultipartAction();
  const setConfirmPopup = useSetAtom(activePopupState("confirm"));
  const setAccountSettingsPopup = useSetAtom(
    activePopupState("account_settings"),
  );
  const navigate = useNavigate();
  const location = useLocation();

  const pathSegments = location.pathname.split("/");
  const view =
    TAB_DEFS.find((t) => pathSegments.includes(t.id))?.id || "accounts";
  const isRootPath = location.pathname === "/" || location.pathname === "";

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyCount = useRef(0);
  const [resettingLimits, setResettingLimits] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [logsPinned, setLogsPinned] = useState(true);
  const [settingsForm, setSettingsForm] = useState<SettingsForm | null>(null);
  const [blocklistDirty, setBlocklistDirty] = useState(false);
  const [logAccount, setLogAccount] = useState<string | null>(null);
  const [accountLogs, setAccountLogs] = useState<LogRow[]>([]);
  const [liveRevision, setLiveRevision] = useState(0);
  const [inboxAccount, setInboxAccount] = useState<string | null>(null);

  const logsRef = useRef<HTMLDivElement | null>(null);
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped when a live push lands so a slower dashboard refetch cannot
  // paint an account as running again after Stop already cleared it.
  const dashboardEpoch = useRef(0);

  const applyDashboard = useCallback(
    (data?: Dashboard | null) => {
      if (!data) return;
      setDashboard(data);
      if (data.config) {
        setSettingsForm((prev) => {
          const next = configToSettingsForm(data.config);
          if (!next) return null;
          if (!prev) return next;
          const editingSettings = Boolean(
            document.activeElement?.closest?.(".ib-settings-form"),
          );
          // Always pull global blocklist from live config unless the user is
          // editing that textarea (Ignore/Resume updates the same list).
          const blocklist = blocklistDirty
            ? prev.blocklist_usernames
            : next.blocklist_usernames;
          if (editingSettings) {
            return {
              ...prev,
              blocklist_usernames: blocklist,
            };
          }
          return { ...next, blocklist_usernames: blocklist };
        });
      }
    },
    [blocklistDirty],
  );

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccess(""), SUCCESS_DISMISS_MS);
  };

  useEffect(
    () => () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    },
    [],
  );

  const refresh = useCallback(
    (soft = false) => {
      const epoch = dashboardEpoch.current;
      if (!soft) {
        setLoading(true);
        setError("");
      }
      return fetchApi(Queries.Dashboard, {}).then((res) => {
        if (epoch !== dashboardEpoch.current) {
          if (!soft) setLoading(false);
          return;
        }
        if (res?.errors) {
          setError(res.errors[0]?.message || "Failed to load Instabot");
        } else if (res?.dashboard) {
          applyDashboard(res.dashboard);
        }
        if (!soft) setLoading(false);
      });
    },
    [fetchApi, applyDashboard],
  );

  const processRunning = Boolean(dashboard?.process?.running);

  const refreshAccountLogs = useCallback(
    (username?: string | null) => {
      if (!username) {
        setAccountLogs([]);
        return Promise.resolve();
      }
      return fetchApi(Queries.Logs, {
        account: username,
        limit: 400,
      }).then((res) => {
        if (res?.logs) setAccountLogs(res.logs);
      });
    },
    [fetchApi],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial dashboard load
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- log source change
    setLogsPinned(true);
    refreshAccountLogs(logAccount);
  }, [logAccount, refreshAccountLogs]);

  useEffect(() => {
    liveDashboard.run((dash) => {
      dashboardEpoch.current += 1;
      applyDashboard(dash);
      setLiveRevision((n) => n + 1);
    });
    return () => liveDashboard.end();
  }, [applyDashboard]);

  useEffect(() => {
    const onConnect = () => {
      void refresh(true);
    };
    window.addEventListener("instabot-api-connected", onConnect);
    return () => window.removeEventListener("instabot-api-connected", onConnect);
  }, [refresh]);

  useEffect(() => {
    liveLog.run(logAccount, (row) => {
      if (!row?.line) return;
      if (logAccount) {
        setAccountLogs((prev) => {
          const next = [...prev, row];
          return next.length > 800 ? next.slice(-800) : next;
        });
      } else {
        setDashboard((prev) => {
          if (!prev) return prev;
          const logs = [...(prev.logs || []), row];
          return {
            ...prev,
            logs: logs.length > 400 ? logs.slice(-400) : logs,
          };
        });
      }
    });
    return () => liveLog.end();
  }, [logAccount]);

  useEffect(() => {
    const el = logsRef.current;
    if (!el || !logsPinned) return;
    el.scrollTop = el.scrollHeight;
  }, [dashboard?.logs, accountLogs, logAccount, logsPinned]);

  const onLogsScroll = () => {
    const el = logsRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setLogsPinned(nearBottom);
  };

  const runMutation = async (
    mutation: string,
    variables?: Record<string, unknown>,
  ): Promise<ApiResult | null> => {
    busyCount.current += 1;
    setBusy(true);
    setError("");
    setSuccess("");
    const epoch = dashboardEpoch.current;
    try {
      const res = await fetchApi(mutation, variables);
      if (res?.errors) {
        setError(res.errors[0]?.message || "Request failed");
      }
      const dash = await fetchApi(Queries.Dashboard, {});
      if (epoch === dashboardEpoch.current && dash?.dashboard) {
        applyDashboard(dash.dashboard);
      }
      return res?.errors ? null : res;
    } finally {
      busyCount.current = Math.max(0, busyCount.current - 1);
      if (busyCount.current === 0) setBusy(false);
    }
  };

  const onStart = async (once = false) => {
    const res = await runMutation(Mutations.Start, { once });
    const result = res?.start;
    if (result && result.success === false) {
      setError(result.message || "Failed to start Instabot");
    } else if (result?.success) {
      flashSuccess(result.message || (once ? "Run once started" : "Started"));
    }
  };

  const onStop = async () => {
    const res = await runMutation(Mutations.Stop, {});
    const result = res?.stop;
    if (result && result.success === false) {
      setError(result.message || "Failed to stop Instabot");
    } else if (result?.success) {
      flashSuccess(result.message || "Stopped");
    }
  };

  const onToggleProcess = async () => {
    if (processRunning) await onStop();
    else await onStart(false);
  };

  const onPauseAll = async (pause: boolean) => {
    const res = await runMutation(Mutations.PauseAll, { pause });
    const result = res?.pause_all;
    if (res?.errors) {
      setError(res.errors[0]?.message || "Failed to toggle pause");
      return;
    }
    if (result && result.success === false) {
      setError(result.message || "Failed to toggle pause");
    } else if (result?.success) {
      flashSuccess(result.message || (pause ? "Paused all bots" : "Resumed all bots"));
    }
  };

  const onStartAccount = async (username: string) => {
    const res = await runMutation(Mutations.StartAccount, {
      username,
    });
    if (res?.errors) {
      setError(res.errors[0]?.message || "Failed to start account");
      return;
    }
    if (res?.start_account) {
      flashSuccess(`Started ${username}`);
    }
  };

  const onStopAccount = async (username: string) => {
    const res = await runMutation(Mutations.StopAccount, {
      username,
    });
    if (res?.errors) {
      setError(res.errors[0]?.message || "Failed to stop account");
      return;
    }
    const stopped = res?.stop_account;
    if (!stopped) return;
    dashboardEpoch.current += 1;
    setDashboard((prev) => {
      if (!prev) return prev;
      const accounts = (prev.accounts || []).map((account) =>
        account.username === username
          ? { ...account, ...stopped, process_running: false, process_pid: null }
          : account,
      );
      const runningCount = accounts.filter((account) => account.process_running)
        .length;
      return {
        ...prev,
        accounts,
        process: prev.process
          ? {
              ...prev.process,
              running: runningCount > 0,
              running_count: runningCount,
              status: runningCount > 0 ? prev.process.status : "STOPPED",
            }
          : prev.process,
      };
    });
    flashSuccess(`Stopped ${username}`);
  };

  const onSubmitChallenge = async (code: string): Promise<boolean> => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Enter the verification code");
      return false;
    }
    const res = await runMutation(Mutations.SubmitChallenge, {
      code: trimmed,
      challenge_id: dashboard?.challenge?.id || null,
    });
    if (res && !res.errors) {
      flashSuccess(
        dashboard?.challenge?.code_submitted
          ? "Code updated — waiting for Instagram…"
          : "Code submitted — waiting for Instagram…",
      );
      return true;
    }
    return false;
  };

  const onCancelChallenge = async (): Promise<boolean> => {
    const res = await runMutation(Mutations.CancelChallenge, {});
    if (res && !res.errors) {
      flashSuccess("Challenge cancelled");
      return true;
    }
    return false;
  };

  const saveSettingsForm = async (): Promise<void> => {
    if (!settingsForm) return;
    let blocklist = String(settingsForm.blocklist_usernames || "")
      .split(/[\n,]+/)
      .map((s) => s.replace(/^@/, "").trim().toLowerCase())
      .filter(Boolean);
    // If the textarea wasn't edited, refetch so Followers Ignore isn't wiped.
    if (!blocklistDirty) {
      try {
        const dash = await fetchApi(Queries.Dashboard, {});
        const latest = dash?.dashboard?.config?.blocklist_usernames;
        if (Array.isArray(latest)) {
          blocklist = latest
            .map((s: unknown) =>
              String(s || "")
                .replace(/^@/, "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean);
        }
      } catch {
        /* keep form list */
      }
    }
    const batch = Math.round(Number(settingsForm.rotation_batch_size) || 5);
    const minutes = Math.round(
      Number(settingsForm.rotation_shift_minutes) || 30,
    );
    const width = Math.round(Number(settingsForm.browser_max_width) || 1700);
    const height = Math.round(Number(settingsForm.browser_max_height) || 900);
    const input = {
      max_concurrent_sessions: Math.max(
        1,
        Number(settingsForm.max_concurrent_sessions) || 10,
      ),
      browser_max_width: Math.min(3840, Math.max(640, width)),
      browser_max_height: Math.min(3840, Math.max(640, height)),
      rotation_enabled: Boolean(settingsForm.rotation_enabled),
      rotation_batch_size: Math.min(500, Math.max(1, batch)),
      rotation_shift_minutes: Math.min(1440, Math.max(1, minutes)),
      hide_suspended_accounts: Boolean(settingsForm.hide_suspended_accounts),
      blocklist_usernames: blocklist,
    };
    const res = await runMutation(Mutations.UpdateConfig, { input });
    if (res?.update_config) {
      setBlocklistDirty(false);
      const hideSuspended = Boolean(settingsForm.hide_suspended_accounts);
      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              config: {
                ...prev.config,
                hide_suspended_accounts: hideSuspended,
              },
            }
          : prev,
      );
      flashSuccess("Settings saved — applied on the next bot cycle");
    }
  };

  const onSaveSettings = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await saveSettingsForm();
  };

  const onRecalculateViewports = async () => {
    // Caps must be persisted before the rewrite reads them.
    await saveSettingsForm();
    const res = await runMutation(Mutations.RecalculateViewports, {});
    if (res?.recalculate_viewports?.success) {
      flashSuccess(
        `Viewports recalculated — ${res.recalculate_viewports.touched ?? 0} account(s) updated`,
      );
    }
  };

  const onResetDailyLimits = async () => {
    setResettingLimits(true);
    try {
      const res = await runMutation(Mutations.ResetDailyLimits, {});
      if (res?.reset_daily_limits?.success) {
        flashSuccess(
          `Daily limits reset — ${res.reset_daily_limits.touched ?? 0} account(s)`,
        );
      }
    } finally {
      setResettingLimits(false);
    }
  };

  const setSetting = <K extends keyof SettingsForm>(
    key: K,
    value: SettingsForm[K],
  ) => {
    if (key === "blocklist_usernames") setBlocklistDirty(true);
    setSettingsForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const copyLogs = async () => {
    const rows = visibleLogs(logAccount ? accountLogs : dashboard?.logs);
    const text = rows.map((r) => `[${r.at}] ${r.line}`).join("\n");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Could not copy logs to clipboard");
    }
  };

  const onOpenMessages = (username?: string | null) => {
    setInboxAccount(username || null);
    navigate("/messages");
  };

  const onOpenAccountSettings = (acc: AccountRow) => {
    if (!acc?.username) return;
    const list = dashboard?.accounts || [];
    const confirmRemove = (username: string) => {
      const account = list.find((a) => a.username === username);
      if (account?.process_running) {
        setError("Stop this account before removing it");
        return;
      }
      setConfirmPopup({
        active: true,
        message: `Remove Instagram account "${username}" from Instabot?`,
        onConfirm: () => {
          runMutation(Mutations.RemoveAccount, { username }).then((res) => {
            if (res && !res.errors) flashSuccess(`Removed ${username}`);
          });
        },
      });
    };
    const confirmClearSession = (username: string) => {
      const account = list.find((a) => a.username === username);
      if (account?.process_running) {
        setError("Stop this account before logging out");
        return;
      }
      setConfirmPopup({
        active: true,
        message:
          `Log out of Instagram for "${username}"? ` +
          "This clears the saved browser session. Next start will need to sign in again.",
        onConfirm: () => {
          runMutation(Mutations.ClearSession, { username }).then((res) => {
            if (res && !res.errors) {
              flashSuccess(`Logged out ${username}`);
            }
          });
        },
      });
    };
    setAccountSettingsPopup({
      active: true,
      account: acc,
      fetchApi,
      fetchMultipart,
      runMutation,
      flashSuccess,
      setError,
      busy,
      processRunning,
      onSave: async (input: Record<string, unknown>) => {
        const res = await runMutation(Mutations.UpsertAccount, {
          input,
        });
        if (res && !res.errors) flashSuccess(`Updated ${acc.username}`);
      },
      onDelete: () => confirmRemove(acc.username),
      onClearSession: () => confirmClearSession(acc.username),
    });
  };

  const allAccounts = dashboard?.accounts ?? NO_ACCOUNTS;
  const hideSuspended = dashboard?.config?.hide_suspended_accounts === true;
  const hiddenAccounts = useMemo(
    () => (hideSuspended ? allAccounts.filter(isSuspendedAccount) : NO_ACCOUNTS),
    [allAccounts, hideSuspended],
  );
  const accounts = useMemo(
    () =>
      hideSuspended
        ? allAccounts.filter((account) => !isSuspendedAccount(account))
        : allAccounts,
    [allAccounts, hideSuspended],
  );
  const visibleLogs = useCallback(
    (rows?: LogRow[] | null) => {
      if (!hiddenAccounts.length) return rows || [];
      return (rows || []).filter(
        (row) =>
          !accountKeyHidden(row.account, hiddenAccounts) &&
          !mentionsHiddenAccount(row.line, hiddenAccounts),
      );
    },
    [hiddenAccounts],
  );
  const proc = useMemo(() => {
    const current = dashboard?.process;
    if (!current || !hiddenAccounts.length) return current;
    const hiddenNames = new Set(
      hiddenAccounts.map((account) => account.username),
    );
    return {
      ...current,
      rotation_wave: (current.rotation_wave || []).filter(
        (name) => !hiddenNames.has(name),
      ),
      running_count: accounts.filter((account) => account.process_running)
        .length,
    };
  }, [dashboard?.process, hiddenAccounts, accounts]);
  const challenge = useMemo(() => {
    const current = dashboard?.challenge;
    if (
      current?.account &&
      accountKeyHidden(current.account, hiddenAccounts)
    ) {
      return null;
    }
    return current;
  }, [dashboard?.challenge, hiddenAccounts]);
  const logsDashboard = useMemo(() => {
    if (!dashboard || !hiddenAccounts.length) return dashboard;
    return {
      ...dashboard,
      accounts,
      logs: visibleLogs(dashboard.logs),
    };
  }, [dashboard, hiddenAccounts.length, accounts, visibleLogs]);

  useEffect(() => {
    if (logAccount && accountKeyHidden(logAccount, hiddenAccounts)) {
      setLogAccount(null);
    }
    if (inboxAccount && accountKeyHidden(inboxAccount, hiddenAccounts)) {
      setInboxAccount(null);
    }
  }, [hiddenAccounts, logAccount, inboxAccount]);

  const messagesAlertCount = accounts.reduce(
    (n, a) => n + (a.failed_outbound_count || 0),
    0,
  );
  const accountsAlertCount = accounts.filter((a) => {
    const st = String(a.session_status || "").toLowerCase();
    if (st === "banned" || st === "needs_login" || st === "challenge")
      return true;
    if (a.enabled !== false && !a.has_session) return true;
    const watching = a.watcher_status && a.watcher_status !== "stopped";
    if (
      watching &&
      (a.inbox_tab_ok === false ||
        a.followers_tab_ok === false ||
        a.requests_tab_ok === false)
    ) {
      return true;
    }
    return false;
  }).length;

  if (isRootPath) {
    return <Navigate to="/accounts" replace />;
  }

  return (
    <div className="ib-app-shell ib-shell">
      <HeaderAlert
        error={error}
        success={success}
        onDismiss={() => {
          if (error) setError("");
          else setSuccess("");
        }}
      />

      <div className="ib-content">
        <ChallengeBanner
          challenge={challenge}
          busy={busy}
          onSubmit={onSubmitChallenge}
          onCancel={onCancelChallenge}
          onEmptyCode={() => setError("Enter the verification code")}
        />

        <div className="ib-tabbar">
          <div className="ib-tabs">
            {TAB_DEFS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`ib-tab-btn${
                  view === t.id ? " ib-tab-btn--active" : ""
                }`}
                onClick={() => navigate(`/${t.id}`)}
              >
                <TabIcon d={t.icon} />
                {t.label}
                {t.id === "messages" && messagesAlertCount > 0 && (
                  <span className="ib-tab-badge" title="Failed outbound">
                    {messagesAlertCount}
                  </span>
                )}
                {t.id === "accounts" && accountsAlertCount > 0 && (
                  <span
                    className="ib-tab-badge"
                    title="Accounts needing attention"
                  >
                    {accountsAlertCount}
                  </span>
                )}
              </button>
            ))}
          </div>
          {onLogout && (
            <button
              type="button"
              className="ib-tab-btn ib-logout-btn"
              onClick={onLogout}
              title="Log out"
            >
              <TabIcon d="M15 12H3m0 0l4-4m-4 4l4 4M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" />
              Log out
            </button>
          )}
        </div>
        <hr className="aff-divider" />

        <div className="ib-view">
          {view === "accounts" && (
            <Accounts
              accounts={accounts}
              processRunning={processRunning}
              busy={busy}
              loading={loading}
              fetchApi={fetchApi}
              fetchMultipart={fetchMultipart}
              runMutation={runMutation}
              flashSuccess={flashSuccess}
              setError={setError}
              setBusy={setBusy}
              setConfirmPopup={setConfirmPopup}
              onOpenMessages={onOpenMessages}
              refresh={refresh}
              hideSuspended={hideSuspended}
            />
          )}
          {view === "phones" && (
            <Phones
              accounts={accounts}
              fetchApi={fetchApi}
              setError={setError}
              flashSuccess={flashSuccess}
            />
          )}
          {view === "models" && (
            <Models
              fetchApi={fetchApi}
              fetchMultipart={fetchMultipart}
              busy={busy}
              setBusy={setBusy}
              setError={setError}
              flashSuccess={flashSuccess}
            />
          )}
          {view === "progress" && (
            <Progress
              accounts={accounts}
              proc={proc}
              processRunning={processRunning}
              busy={busy}
              loading={loading}
              config={dashboard?.config}
              onToggleProcess={onToggleProcess}
              onPauseAll={onPauseAll}
              onStartAccount={onStartAccount}
              onStopAccount={onStopAccount}
            />
          )}
          {view === "messages" && (
            <Messages
              accounts={accounts}
              loading={loading}
              initialAccount={inboxAccount}
              fetchApi={fetchApi}
              fetchMultipart={fetchMultipart}
              busy={busy}
              setBusy={setBusy}
              setError={setError}
              flashSuccess={flashSuccess}
              liveRevision={liveRevision}
              processRunning={processRunning}
              setConfirmPopup={setConfirmPopup}
              onOpenAccountSettings={onOpenAccountSettings}
            />
          )}
          {view === "followers" && (
            <Followers
              accounts={accounts}
              loadingAccounts={loading}
              fetchApi={fetchApi}
              setError={setError}
              flashSuccess={flashSuccess}
              liveRevision={liveRevision}
            />
          )}
          {view === "migration" && <Migration accounts={accounts} setError={setError} />}
          {view === "logs" && (
            <Logs
              accounts={accounts}
              dashboard={logsDashboard}
              hiddenAccounts={hiddenAccounts}
              logAccount={logAccount}
              setLogAccount={setLogAccount}
              accountLogs={visibleLogs(accountLogs)}
              logsPinned={logsPinned}
              setLogsPinned={setLogsPinned}
              logsRef={logsRef}
              onLogsScroll={onLogsScroll}
              copyLogs={copyLogs}
              loading={loading}
              fetchApi={fetchApi}
              setError={setError}
            />
          )}
          {view === "settings" && (
            <Settings
              settingsForm={settingsForm}
              setSetting={setSetting}
              onSaveSettings={onSaveSettings}
              onRecalculateViewports={onRecalculateViewports}
              onResetDailyLimits={onResetDailyLimits}
              resettingLimits={resettingLimits}
              busy={busy}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppShell;
